import React, { useEffect, useRef, useState } from "react";
import { Check, X, Trophy, RotateCcw, Timer, Hash } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { SoundToggle } from "@/components/SoundToggle";
import { useAuth } from "@/context/AuthContext";
import { reportScore } from "@/lib/gameScores";
import { playRight, playWrong } from "@/lib/feedbackSounds";

const SECONDS = 60;
const KEY = "jt-accidental-spotter-best-60";
const RANGE = { treble: ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5", "A5"], bass: ["E2", "F2", "G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3", "B3", "C4"] };
// No E♯, B♯, C♭ or F♭ — those white-key spellings come later.
const OK = { "#": "CDFGA", b: "DEGAB" };
const LETTERS = "CDEFGAB";
const SHARP_OF = { "C#": "Db", "D#": "Eb", "F#": "Gb", "G#": "Ab", "A#": "Bb" };
const ENH = { ...SHARP_OF, ...Object.fromEntries(Object.entries(SHARP_OF).map(([a, b]) => [b, a])) };
export const accName = (s) => `${s[0]}${{ "#": "♯", b: "♭" }[s[1]] || ""}`;
const pick = (a) => a[Math.floor(Math.random() * a.length)];
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

export const makeAccQ = (prev) => {
  let clef, base, acc;
  do { clef = Math.random() < 0.5 ? "treble" : "bass"; base = pick(RANGE[clef]); acc = Math.random() < 0.5 ? "#" : "b"; } while (!OK[acc].includes(base[0]) || `${clef}${base}${acc}` === prev);
  const name = base[0] + acc;
  const other = acc === "#" ? "b" : "#";
  const near = LETTERS[(LETTERS.indexOf(base[0]) + (acc === "#" ? 1 : 6)) % 7] + acc;
  const wrongs = [...new Set([ENH[name], base[0], OK[other].includes(base[0]) ? base[0] + other : null, OK[acc].includes(near[0]) ? near : null].filter((x) => x && x !== name))];
  return { id: `${clef}${base}${acc}`, clef, pitch: base[0] + acc + base.slice(-1), name, options: shuffle([name, ...shuffle(wrongs).slice(0, 3)]) };
};

// Lesson 5: 60-second accidental spotting on both staves; beat your best score.
export const AccidentalSpotter = ({ testId = "accidental-spotter" }) => {
  const [q, setQ] = useState(null);
  const [left, setLeft] = useState(SECONDS);
  const [score, setScore] = useState(0);
  const [tries, setTries] = useState(0);
  const [picked, setPicked] = useState(null);
  const [over, setOver] = useState(false);
  const [best, setBest] = useState(() => Number(localStorage.getItem(KEY) || 0));
  const [newBest, setNewBest] = useState(false);
  const { user } = useAuth();
  const scoreRef = useRef(0);
  const nextT = useRef(null);
  const endsAt = useRef(0);
  useEffect(() => () => clearTimeout(nextT.current), []);

  useEffect(() => {
    if (!q || over) return undefined;
    const t = setInterval(() => setLeft(Math.max(0, Math.ceil((endsAt.current - Date.now()) / 1000))), 250);
    return () => clearInterval(t);
  }, [q === null, over]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!q || over || left > 0) return;
    clearTimeout(nextT.current);
    setOver(true);
    const s = scoreRef.current;
    if (s > best) { setBest(s); setNewBest(true); localStorage.setItem(KEY, String(s)); }
    if (user) reportScore("accidental-spotter", "Accidental spotting", "round", s);
  }, [q, left, over, best, user]);

  const start = () => { endsAt.current = Date.now() + SECONDS * 1000; scoreRef.current = 0; setScore(0); setTries(0); setLeft(SECONDS); setOver(false); setNewBest(false); setPicked(null); setQ(makeAccQ()); };
  const choose = (o) => {
    if (picked || over) return;
    setPicked(o);
    setTries((t) => t + 1);
    const ok = o === q.name;
    if (ok) { scoreRef.current += 1; setScore(scoreRef.current); playRight(); } else playWrong();
    nextT.current = setTimeout(() => { setPicked(null); setQ((cur) => makeAccQ(cur.id)); }, ok ? 650 : 1500);
  };
  const ok = picked && picked === q.name;

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Hash size={14} /> Accidental spotter</p>
        <p className="flex items-center gap-3 text-xs text-muted-foreground">
          {q && <span data-testid={`${testId}-progress`}>Score <b className="text-foreground">{score}</b> / {tries}</span>}
          <SoundToggle testId={`${testId}-sound-toggle`} />
          <span className="flex items-center gap-1" data-testid={`${testId}-best`}><Trophy size={12} className="text-primary" /> Best: {best}</span>
        </p>
      </div>
      {!q ? (
        <div className="mt-3">
          <p className="text-sm">You have {SECONDS} seconds. A note with a sharp or a flat appears on the treble or bass stave: tap its name. Watch the clef! How many can you get right?</p>
          <button type="button" onClick={start} data-testid={`${testId}-start`} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Start</button>
        </div>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-3" data-testid={`${testId}-timer`}>
            <Timer size={16} className={left <= 10 && !over ? "text-red-500" : "text-primary"} />
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-border">
              <div className={`h-full transition-[width] duration-1000 ease-linear ${left <= 10 ? "bg-red-500" : "bg-primary"}`} style={{ width: `${(left / SECONDS) * 100}%` }} />
            </div>
            <span className={`w-12 text-right font-mono text-sm font-bold ${left <= 10 ? "text-red-500" : ""}`} data-testid={`${testId}-seconds`}>{left}s</span>
          </div>
          {over ? (
            <div className="mt-4 text-center" data-testid={`${testId}-over`}>
              <p className="text-lg font-semibold">Time's up! You named <span className="text-primary">{score}</span> note{score === 1 ? "" : "s"} correctly{tries ? ` out of ${tries}` : ""}.</p>
              {newBest && <p className="mt-1 text-sm font-bold text-primary" data-testid={`${testId}-new-best`}>New best score!</p>}
              <button type="button" onClick={start} data-testid={`${testId}-again`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground"><RotateCcw size={13} /> Play again</button>
            </div>
          ) : (
            <>
              <div className={`mx-auto mt-3 max-w-xs rounded-xl border-2 bg-background/60 p-2 transition-colors ${picked ? (ok ? "border-emerald-500" : "border-red-500") : "border-border"}`} data-testid={`${testId}-card`} data-clef={q.clef} data-pitch={q.pitch}>
                <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{q.clef} clef</p>
                <StaffViewer key={q.id + tries} clef={q.clef} notes={[{ pitch: q.pitch, label: "", duration: "whole" }]} width={220} testId={`${testId}-staff`} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {q.options.map((o, k) => {
                  const st = picked && (o === q.name ? "right" : o === picked ? "wrong" : "");
                  return (
                    <button key={o} type="button" onClick={() => choose(o)} data-testid={`${testId}-option-${k}`} data-correct={o === q.name}
                      className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-base font-bold transition-colors ${st === "right" ? "border-emerald-500 bg-emerald-500/15" : st === "wrong" ? "border-red-500 bg-red-500/20 text-red-500" : "border-border hover:border-primary/60"}`}>
                      {accName(o)}{st === "right" && <Check size={15} className="text-emerald-500" />}{st === "wrong" && <X size={15} className="text-red-500" />}
                    </button>
                  );
                })}
              </div>
              {picked && (
                <p className={`mt-3 inline-block rounded-full px-4 py-1.5 text-sm font-bold ${ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`} data-testid={`${testId}-feedback`}>
                  {ok ? "Correct!" : `Not quite: it's ${accName(q.name)}${ENH[picked] === q.name ? ` — ${accName(picked)} is the same key, but this note is written as ${accName(q.name)}` : ""}.`}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
